/**
 * Flow: a Sankey of from -> to transitions for one domain, plus the table
 * behind it. The Sankey cannot draw a cycle, and real workflows have them
 * (a case sent back goes to where it came from), so a link that would close a
 * loop lands on its own copy of the target node, labelled as a return.
 */
import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, Sankey, Tooltip } from 'recharts';
import { Domain, FlowLink, Flows, WindowParams, flowApi } from './flowApi';
import { C, DOMAINS, DOMAIN_COLOR, DOMAIN_LABEL, DOMAIN_MARKER, DOMAIN_NOTE, tooltipProps } from './chartTheme';
import { ChartCard, EmptyNote, NOT_RECORDED, Remote, fmtHours, fmtN, stateLabel, useRemote } from './flowUi';
import { LivingLegend, Segmented } from '../../../components/tide';

export interface SankeyData {
  nodes: Array<{ name: string }>;
  links: Array<{ source: number; target: number; value: number }>;
}

const RETURN = 'return:';

/**
 * Build Sankey nodes and links from transitions, strongest first. A link
 * whose target can already reach its source (or is its own source) would
 * close a cycle, so it targets a separate "return:" node instead; that node
 * has no outgoing links, so the graph stays acyclic by construction.
 */
export function buildSankey(links: FlowLink[]): SankeyData {
  const index = new Map<string, number>();
  const nodes: Array<{ name: string }> = [];
  const out = new Map<string, Set<string>>();
  const node = (key: string, name: string) => {
    if (!index.has(key)) { index.set(key, nodes.length); nodes.push({ name }); }
    return index.get(key) as number;
  };
  const reaches = (from: string, to: string): boolean => {
    const seen = new Set<string>();
    const stack = [from];
    while (stack.length) {
      const k = stack.pop() as string;
      if (k === to) return true;
      if (seen.has(k)) continue;
      seen.add(k);
      out.get(k)?.forEach((n) => stack.push(n));
    }
    return false;
  };

  const out_links: SankeyData['links'] = [];
  [...links].sort((a, b) => b.count - a.count).forEach((l) => {
    if (l.count <= 0) return;
    const fromKey = l.from == null ? NOT_RECORDED : l.from;
    const toKey = l.to == null ? NOT_RECORDED : l.to;
    const cyclic = fromKey === toKey || reaches(toKey, fromKey);
    const source = node(fromKey, stateLabel(l.from));
    const target = cyclic ? node(RETURN + toKey, `${stateLabel(l.to)} (returned)`) : node(toKey, stateLabel(l.to));
    if (!cyclic) {
      if (!out.has(fromKey)) out.set(fromKey, new Set());
      (out.get(fromKey) as Set<string>).add(toKey);
    }
    out_links.push({ source, target, value: l.count });
  });
  return { nodes, links: out_links };
}

export default function FlowView({ window: w, origin }: { window: WindowParams; origin: string }) {
  const { state, retry } = useRemote<Flows>(`flows|${w.from}|${w.to}|${origin}`, () => flowApi.flows(w, origin || undefined));
  return <Remote state={state} retry={retry} skeleton="chart">{(data) => <FlowBody data={data} />}</Remote>;
}

function FlowBody({ data }: { data: Flows }) {
  const [domain, setDomain] = useState<Domain>('PA');
  const links = useMemo(() => data.links.filter((l) => l.domain === domain), [data, domain]);
  const sankey = useMemo(() => buildSankey(links), [links]);
  const sorted = useMemo(() => [...links].sort((a, b) => b.count - a.count), [links]);
  const color = DOMAIN_COLOR[domain];
  const height = Math.max(260, Math.min(640, sankey.nodes.length * 34));
  const steps = links.reduce((s, l) => s + l.count, 0);
  const returned = sankey.nodes.filter((n) => n.name.endsWith('(returned)')).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <Segmented
          label="Domain"
          options={DOMAINS.map((d) => ({ value: d, label: DOMAIN_LABEL[d], marker: { shape: DOMAIN_MARKER[d], color: DOMAIN_COLOR[d] } }))}
          value={domain}
          onChange={(v) => setDomain(v as Domain)}
        />
        <p className="tide-small tide-ink-2" style={{ margin: 0, paddingBottom: 8 }}>Status, handoff, close and reopen steps in the window. The 60 strongest transitions overall are returned.</p>
      </div>

      {links.length === 0 ? <EmptyNote reason="filtered">No transitions for {DOMAIN_LABEL[domain]} in this window.</EmptyNote> : (
        <>
          <ChartCard
            side
            title={`${DOMAIN_LABEL[domain]} transitions`}
            hint="Band width is the number of steps. A state that records come back to appears again as 'returned'."
            summary={`${DOMAIN_LABEL[domain]} transitions: ${sorted.slice(0, 10).map((l) => `${stateLabel(l.from)} to ${stateLabel(l.to)} ${l.count}`).join('; ')}.`}
            legend={(
              <LivingLegend
                stacked
                density="compact"
                title="Reading the bands"
                items={[
                  { key: domain, label: DOMAIN_LABEL[domain], color, marker: DOMAIN_MARKER[domain], description: DOMAIN_NOTE[domain], value: steps, unit: 'steps drawn' },
                  { key: 'returned', label: 'Returned', color, marker: 'hatch', description: 'A state records came back to, drawn as its own node so loops stay readable.', value: returned, unit: returned === 1 ? 'node' : 'nodes' },
                ]}
              />
            )}
          >
            <ResponsiveContainer width="100%" height="100%" minHeight={height}>
              <Sankey
                data={sankey}
                nodePadding={18}
                nodeWidth={12}
                margin={{ top: 8, right: 150, bottom: 8, left: 8 }}
                node={(p: any) => <SankeyNode {...p} color={color} />}
                link={(p: any) => <SankeyLink {...p} color={color} />}
              >
                <Tooltip {...tooltipProps} />
              </Sankey>
            </ResponsiveContainer>
          </ChartCard>

          <section className="tide-panel">
            <header className="tide-panel__head"><h2 className="tide-panel__title">Transitions</h2></header>
            <div className="tide-scroll" tabIndex={0} role="region" aria-label={`${DOMAIN_LABEL[domain]} transitions table (scrolls sideways)`}>
              <table className="tide-table min-w-[560px]">
                <thead>
                  <tr>
                    <th scope="col">From</th>
                    <th scope="col">To</th>
                    <th scope="col" className="tide-num">Count</th>
                    <th scope="col" className="tide-num">Median hours in From</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((l) => (
                    <tr key={`${l.from}>${l.to}`}>
                      <td>{stateLabel(l.from)}</td>
                      <td>{stateLabel(l.to)}</td>
                      <td className="tide-num tabular-nums">{fmtN(l.count)}</td>
                      <td className="tide-num tabular-nums">{fmtHours(l.medianHoursInFrom)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function SankeyNode({ x, y, width, height, payload, color }: any) {
  const label = String(payload?.name ?? '');
  const returned = label.endsWith('(returned)');
  return (
    <g className="recharts-sankey-node">
      <rect x={x} y={y} width={width} height={Math.max(height, 1)} fill={color} rx={3} fillOpacity={returned ? 0.55 : 1} stroke={returned ? color : undefined} strokeDasharray={returned ? '3 2' : undefined} />
      <text x={x + width + 6} y={y + height / 2} dy="0.35em" fontSize={12} fill={C.foreground}>{label.length > 26 ? `${label.slice(0, 25)}…` : label}</text>
    </g>
  );
}

function SankeyLink({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, color }: any) {
  return (
    <path
      d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
      fill="none" stroke={color} strokeWidth={linkWidth} strokeOpacity={0.28}
    />
  );
}
